import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message, Input } from "antd";

import "./stylesResetPassword.css";
import { NAVIGATION_ROUTES } from "../../utils/NavigationUtils";
import { API_ROUTES } from "../../api/ApiRoutes";
import PublicAdminPanelInstance from "../../api/AffiliatePanelInstance";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../utils/ErrorUtils";
import PasswordPolicyChecklist from "../shared/PasswordPolicyChecklist/PasswordPolicyChecklist";
import { usePasswordPolicyValidation } from "../../hooks/usePasswordPolicyValidation";

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const user_name =
    searchParams.get("user_name") ||
    searchParams.get("email") ||
    searchParams.get("username") ||
    "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInlinePolicyError, setShowInlinePolicyError] = useState(false);

  useEffect(() => {
    if (!user_name?.trim()) {
      message.warning(AppStrings.PAM_RESET_LINK_INVALID);
      navigate(NAVIGATION_ROUTES?.LOGIN, { replace: true });
    }
  }, [user_name, navigate]);

  const passwordPolicy = usePasswordPolicyValidation({
    email: user_name,
    password,
    audience: "staff",
    enabled: Boolean(user_name?.trim()),
  });

  const confirmationCodeValidation = useMemo(() => ({
    hasMinLength: confirmationCode.length >= 6,
  }), [confirmationCode]);

  const isButtonDisabled =
    isSubmitting || !passwordPolicy.allPassed || confirmPassword !== password || !confirmationCodeValidation.hasMinLength;


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!password || !confirmPassword) {
      message.error(AppStrings.PAM_PASSWORD_FIELDS_REQUIRED);
      setIsSubmitting(false);
      return;
    }

    if (!passwordPolicy.allPassed) {
      message.error(AppStrings.PAM_PASSWORD_REQUIREMENTS_NOT_MET);
      setIsSubmitting(false);
      return;
    }

    const params = {
      confirmation_code: confirmationCode,
      new_password: password,
    }

    try {
      await PublicAdminPanelInstance.post(API_ROUTES?.RESET_PASSWORD, params, {
        params: { user_name }
      });
      message.success(AppStrings.PAM_RESET_PASSWORD_SUCCESS);
      navigate(NAVIGATION_ROUTES?.LOGIN, { replace: true });
    } catch (error) {
      const data = error?.response?.data;
      if (
        data?.error_code === "INVALID_PASSWORD" &&
        passwordPolicy.applyPolicyResult(data?.detail)
      ) {
        setShowInlinePolicyError(true);
        return;
      }
      message.error(
        getApiErrorMessage(
          error,
          AppStrings.PAM_RESET_PASSWORD_INVALID_OR_EXPIRED,
        ),
      );
      // navigate(NAVIGATION_ROUTES?.LOGIN, { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user_name?.trim()) {
    return null;
  }

  const onChange = text => {
    setConfirmationCode(text);
  };
  const onInput = value => {
    setConfirmationCode(value);
  };
  const sharedProps = {
    onChange,
    onInput,
  };

  return (
    <div className="reset-password-container">
      <form
        className="reset-password-card"
        onSubmit={handleSubmit}
        data-testid="reset-password-form"
      >
        <div className="reset-password-card-header">
          <h1>{AppStrings.PAM_RESET_PASSWORD_TITLE}</h1>
          <p>{AppStrings.PAM_RESET_PASSWORD_SUBTITLE}</p>
        </div>
        
        <div className="form-group">
          <label>{AppStrings.PAM_RESET_PASSWORD_CONFIRMATION_CODE}</label>
          <Input.OTP formatter={str => str.toUpperCase()} {...sharedProps} className="confirmation-code-input" data-testid="confirmation-code-input"/>
        </div>

        <div className="form-group">  
          <label>{AppStrings.PAM_RESET_PASSWORD_NEW_LABEL}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setShowInlinePolicyError(false);
            }}
            placeholder={AppStrings.PAM_RESET_PASSWORD_NEW_PLACEHOLDER}
          />

          <PasswordPolicyChecklist
            result={passwordPolicy.policyResult}
            isCurrent={passwordPolicy.isCurrent}
            isPending={passwordPolicy.isPending}
            hasError={passwordPolicy.hasError}
            showInlineError={showInlinePolicyError}
          />
        </div>

        <div className="form-group">
          <label>{AppStrings.PAM_RESET_PASSWORD_CONFIRM_LABEL}</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={AppStrings.PAM_RESET_PASSWORD_CONFIRM_PLACEHOLDER}
          />
        </div>

        <button
          type="submit"
          disabled={isButtonDisabled}
          className={`reset-password-button ${isButtonDisabled ? "disabled" : ""}`}
        >
          {isSubmitting
            ? AppStrings.SAVING
            : AppStrings.PAM_RESET_PASSWORD_TITLE}
        </button>
      </form>
    </div>
  );
}

export default ResetPassword;
